// src/pages/NotificationSettings.tsx
import React, { useEffect, useState, useMemo } from 'react';
import {
	Card,
	CardBody,
	Input,
	Spinner,
	Button,
	UncontrolledTooltip,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	UncontrolledDropdown,
	FormGroup,
	Label,
	FormText,
} from 'reactstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ApplicationState } from 'store';
import { userGetRequest } from 'store/auth/action';
import { userUpdateRequest } from 'store/user/action';
import { createSelector, ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { useParams } from 'react-router-dom';
import { parse, format } from 'date-fns';
import { createSelectStyles } from 'helpers/reactSelectLayout';

import {
	CATEGORIES,
	CHANNELS,
	getMeta,
	isDigestTemplate
} from 'helpers/notifications/notificationConfig';
import { withTranslation } from 'react-i18next';

type ChannelKey = string;
type TemplateKey = string;

type Mode = 'custom' | 'daily';
interface ReminderSetting {
	enabled: boolean;
	mode: Mode;
	offsets: number[];
	digestTime: string;
}

interface ChannelPermission {
	enabled: boolean;
	default: boolean;
}

type AdminNotificationSettingsMap = Record<
	TemplateKey,
	{
		channels: Record<ChannelKey, ChannelPermission>;
		reminder: ReminderSetting & { overrideable: boolean };
	}
>;

interface UserTemplateSetting {
	channels: Record<ChannelKey, boolean>;
	reminder: ReminderSetting;
}
type UserSettingsMap = Record<TemplateKey, UserTemplateSetting>;

// 1) Build admin defaults
function buildAdminDefault(): AdminNotificationSettingsMap {
	const def = {} as AdminNotificationSettingsMap;
	CATEGORIES.forEach(cat =>
		cat.templates.forEach(tpl => {
			const channels: Record<ChannelKey, ChannelPermission> = {};
			CHANNELS.forEach(ch => {
				channels[ch.key] = { enabled: ch.defaultEnabled, default: ch.defaultEnabled };
			});
			def[tpl.key] = {
				channels,
				reminder: {
					enabled: false,
					overrideable: false,
					mode: 'custom',
					offsets: [],
					digestTime: '',
				},
			};
		})
	);
	return def;
}

// 2) Merge tenant JSON into admin defaults
function mergeAdminRaw(rawJson: string): AdminNotificationSettingsMap {
	const merged = buildAdminDefault();
	let raw: any = {};
	try {
		raw = JSON.parse(rawJson);
	} catch { }
	Object.entries(raw).forEach(([tplKey, cfg]: any) => {
		if (!merged[tplKey]) return;
		if (cfg.channels) {
			Object.entries(cfg.channels).forEach(([chKey, perm]: any) => {
				if (merged[tplKey].channels[chKey]) {
					merged[tplKey].channels[chKey] = perm;
				}
			});
		}
		if (cfg.reminder) {
			const r = merged[tplKey].reminder;
			merged[tplKey].reminder = {
				enabled: cfg.reminder.enabled ?? r.enabled,
				overrideable: cfg.reminder.overrideable ?? r.overrideable,
				mode: cfg.reminder.mode ?? r.mode,
				offsets: cfg.reminder.offsets ?? r.offsets,
				digestTime: cfg.reminder.digestTime ?? r.digestTime,
			};
		}
	});
	return merged;
}

// 3) Merge user JSON on top of admin for initial state
function mergeUserRaw(
	rawJson: string,
	adminMap: AdminNotificationSettingsMap
): UserSettingsMap {
	const base = JSON.parse(rawJson || '{}');
	const merged = {} as UserSettingsMap;

	Object.entries(adminMap).forEach(([tplKey, adminCfg]) => {
		// Channels
		const userChs: Record<ChannelKey, boolean> = {};
		Object.entries(adminCfg.channels).forEach(([chKey, perm]) => {
			userChs[chKey] = perm.enabled
				? (base[tplKey]?.channels?.[chKey] ?? perm.default)
				: perm.default;
		});

		// Reminders
		const a = adminCfg.reminder;
		let rem: ReminderSetting = {
			enabled: a.enabled,
			mode: a.mode,
			offsets: a.offsets,
			digestTime: a.digestTime,
		};
		if (a.overrideable && base[tplKey]?.reminder) {
			const b: ReminderSetting = base[tplKey].reminder;
			rem = {
				enabled: b.enabled ?? rem.enabled,
				mode: b.mode ?? rem.mode,
				offsets: b.offsets ?? rem.offsets,
				digestTime: b.digestTime ?? rem.digestTime,
			};
		}
		if (!a.enabled) rem.enabled = false;

		merged[tplKey] = { channels: userChs, reminder: rem };
	});

	return merged;
}

// above your component, add:
const OFFSET_VALUES = [86400, 3600, 900, 600, 300];
// common times you might want to offer
const COMMON_DIGEST_TIMES = Array.from({ length: 24 }, (_, i) =>
	String(i).padStart(2, '0') + ':00'
);

function prettyTime(hhmm: string) {
	try {
		return format(parse(hhmm, 'HH:mm', new Date()), 'h:mm aa');
	} catch {
		return hhmm;
	}
}

function formatOffsetLabel(seconds: number): string {
	if (seconds % 86400 === 0) {
		const days = seconds / 86400;
		return `${days} day${days > 1 ? 's' : ''} before`;
	}
	if (seconds % 3600 === 0) {
		const hrs = seconds / 3600;
		return `${hrs} hour${hrs > 1 ? 's' : ''} before`;
	}
	const mins = seconds / 60;
	return `${mins} minute${mins > 1 ? 's' : ''} before`;
}

const NotificationSettings: React.FC = ({t}: any) => {
	const { id } = useParams<{ id: string }>();
	const dispatch = useDispatch<ThunkDispatch<any, null, Action<string>>>();
	const user = useSelector((s: ApplicationState) => s.auth.userProfile);
	const loading = useSelector((s: ApplicationState) => s.auth.loading);
	const layoutModeType: any = useSelector((state: ApplicationState) => state.layout.layoutModeType);
	  

	const rawAdminJson = useMemo(
		() => user?.tenant?.notificationSettings ?? '{}',
		[user]
	);
	const rawUserJson = useMemo(() => {
		const tId = user?.tenant?.id;
		const ut = user?.userTenants?.find((x: { tenant_id: string; }) => x.tenant_id === tId);
		return ut?.notificationSettings ?? '{}';
	}, [user]);

	const adminMap = useMemo(() => mergeAdminRaw(rawAdminJson), [rawAdminJson]);
	const [settings, setSettings] = useState<UserSettingsMap>(() =>
		mergeUserRaw(rawUserJson, adminMap)
	);

	const customStyles = createSelectStyles(layoutModeType);

	useEffect(() => {
		setSettings(mergeUserRaw(rawUserJson, adminMap));
	}, [rawUserJson, adminMap]);

	const toggleChannel = (tpl: TemplateKey, ch: ChannelKey) =>
		setSettings(s => ({
			...s,
			[tpl]: {
				...s[tpl],
				channels: { ...s[tpl].channels, [ch]: !s[tpl].channels[ch] },
				reminder: s[tpl].reminder,
			},
		}));

	const updateReminder = (tpl: TemplateKey, rem: Partial<ReminderSetting>) =>
		setSettings(s => ({
			...s,
			[tpl]: { ...s[tpl], reminder: { ...s[tpl].reminder, ...rem } },
		}));

	const save = () => {
		dispatch(
			userUpdateRequest(
				id!,
				{ attributes: [{ key: 'user_tenant_notification_settings', value: JSON.stringify(settings) }] },
				() => {
					toast.success('Settings saved.', { position: 'top-center' });
					dispatch(userGetRequest(id!));
				},
				(err: { status: any; }) => {
					toast.error(`Error: ${err.status}`, { position: 'top-center' });
				}
			)
		);
	};

	const reset = () => setSettings(mergeUserRaw('{}', adminMap));

	const bulkToggleCategory = (catKey: string, val: boolean) =>
		setSettings(s => {
			const upd = { ...s };
			CATEGORIES.find(c => c.key === catKey)?.templates.forEach(tpl => {
				CHANNELS.forEach(ch => {
					if (adminMap[tpl.key].channels[ch.key].enabled) {
						upd[tpl.key].channels[ch.key] = val;
					}
				});
			});
			return upd;
		});

	const makeTooltipId = (section: string, tpl: string, ch: string) =>
		`tooltip-${section}-${tpl.replace(/\W/g, '-')}-${ch}`;

	return (
		<Card className="border-0 shadow-sm">
			<CardBody className="pb-4">
				{/* Header & actions */}
				<div className="d-flex justify-content-between mb-4">
					<div>
						<h4><i className="ri-notification-3-line me-2" />{t("notification.notification_preferences")}</h4>
						<p className="text-muted small mb-0">
							{t("notification.notification_info_message")}
						</p>
					</div>
					<div className="d-flex gap-2">
						<Button outline onClick={reset}>{t("notification.reset_to_default")}</Button>
						<Button color="primary" onClick={save} disabled={loading}>
							{loading ? t("common.offcanvas.saving") : t("common.modals.save_changes")}
						</Button>
					</div>
				</div>

				{loading ? (
					<div className="text-center py-5"><Spinner /></div>
				) : (
					<div className="d-flex flex-column gap-4">
						{CATEGORIES.map(cat => {
							// only show the “base” templates, not the digest variants
							const visible = cat.templates.filter(tpl => !isDigestTemplate(tpl.key));
							return (
								<div key={cat.key} className="p-2 rounded bg-light-subtle">
									{/* Category header & bulk */}
									<div className="d-flex justify-content-between align-items-center">
										<div className="d-flex align-items-center gap-2">
											<i className="ri-folder-2-line text-primary" /><h6 className="fw-semibold mb-0">{cat.label}</h6>
										</div>
										<UncontrolledDropdown>
											<DropdownToggle size="sm" color="light">
												<i className="ri-settings-3-line" /> {t("common.table.bulk_actions")}
											</DropdownToggle>
											<DropdownMenu end>
												<DropdownItem header>{t("notification.preferences")}</DropdownItem>
												<DropdownItem onClick={() => bulkToggleCategory(cat.key, true)}>{t("notification.enable_all")}</DropdownItem>
												<DropdownItem onClick={() => bulkToggleCategory(cat.key, false)}>{t("notification.disable_all")}</DropdownItem>
											</DropdownMenu>
										</UncontrolledDropdown>
									</div>
									<p className="text-muted small ms-4 mb-3">{cat.description}</p>

									{/* Templates */}
									<div className="d-flex flex-column gap-3">
										{visible.map(tpl => {
											const meta = getMeta(tpl.key);
											const userCh = settings[tpl.key].channels;
											const adminCh = adminMap[tpl.key].channels;
											const adminRem = adminMap[tpl.key].reminder;
											const userRem = settings[tpl.key].reminder;
											const reminderId = makeTooltipId('reminder', tpl.key, 'reminder');

											return (
												<div key={tpl.key} className="p-2 rounded shadow-sm">
													{/* Channels */}
													<div className="d-flex justify-content-between align-items-center">
														<div className="fw-medium d-flex align-items-center gap-2">
															<i className={`${meta?.icon} fs-5`} /> {tpl.label}
														</div>
														<div className="d-flex flex-wrap gap-4">
															{CHANNELS.map(ch => {
																const allowed = adminCh[ch.key].enabled;
																const checked = userCh[ch.key];
																const id = makeTooltipId('channel', tpl.key, ch.key);
																return (
																	<div key={ch.key} className="form-check form-switch position-relative">
																		<Input type="checkbox"
																			id={`${tpl.key}-${ch.key}`}
																			checked={checked}
																			disabled={!allowed}
																			onChange={() => toggleChannel(tpl.key, ch.key)}
																		/>
																		<label
																			id={id}
																			htmlFor={id}
																			className={`form-check-label small ${!allowed ? 'text-muted opacity-50' : 'text-muted'}`}
																		>
																			{ch.label}
																		</label>
																		{!allowed && (
																			<UncontrolledTooltip placement="top" target={id} delay={{ show: 300, hide: 50 }}>
																				{t("notification.disabled_by_workspace_admin_info_message")}
																			</UncontrolledTooltip>
																		)}
																	</div>
																);
															})}
														</div>
													</div>
													{/* Reminders */}
													{meta?.supportsReminder && (
														<>
															<div id={reminderId} className="border rounded p-3 bg-light-subtle mt-2">
																{/* Master on/off */}
																<FormGroup switch>
																	<Input
																		type="switch"
																		checked={userRem.enabled}
																		disabled={!adminRem.overrideable}
																		onChange={() =>
																			updateReminder(tpl.key, { enabled: !userRem.enabled })
																		}
																	/>
																	<Label>{t("notification.enable_event_reminders")}</Label>
																</FormGroup>
																<FormText color="muted">
																	{t("notification.turn_reminders_on_off_for_this_notification_type")}
																</FormText>

																{userRem.enabled && (
																	<>
																		{/* Style */}
																		<FormGroup className="mt-3">
																			<Label className="small">{t("notification.reminder_style")}</Label>
																			<UncontrolledDropdown disabled={!adminRem.overrideable}>
																				<DropdownToggle tag="button" className="btn bg-dark-subtle w-100 d-flex align-items-center justify-content-between">
																					{userRem.mode === 'custom'
																						? t("notification.multiple_reminders")
																						: t("notification.daily_summary")}
																					<i className="ri-arrow-down-s-line ms-auto" />
																				</DropdownToggle>
																				<DropdownMenu>
																					<DropdownItem
																						active={userRem.mode === 'custom'}
																						onClick={() => updateReminder(tpl.key, { mode: 'custom' })}
																					>
																						{t("notification.multiple_reminders")}
																					</DropdownItem>
																					<DropdownItem
																						active={userRem.mode === 'daily'}
																						onClick={() => updateReminder(tpl.key, { mode: 'daily' })}
																					>
																						{t("notification.daily_summary")}
																					</DropdownItem>
																				</DropdownMenu>
																			</UncontrolledDropdown>
																			<FormText color="muted">
																				{userRem.mode === 'custom'
																					? t("notification.receive_separate_reminder_info_message")
																					: t("notification.get_one_summary_of_all_events_info_message")}
																			</FormText>
																		</FormGroup>

																		{userRem.mode === 'custom' ? (
																			<FormGroup className="mt-2">
																				<Label className="small">{t("notification.when_to_remind")}</Label>
																				<Select
																					isMulti
																					options={OFFSET_VALUES.map(v => ({
																						value: v,
																						label: formatOffsetLabel(v),
																					}))}
																					value={userRem.offsets.map(o => ({
																						value: o,
																						label: formatOffsetLabel(o),
																					}))}
																					isDisabled={!adminRem.overrideable}
																					onChange={(opts: any[]) =>
																						updateReminder(tpl.key, { offsets: opts.map(x => x.value) })
																					}
																					classNamePrefix="react-select"
																					customStyles={customStyles}
																				/>

																				<FormText color="muted">
																					{t("notification.select_one_more_times_to_be_notified_info_message")}
																				</FormText>
																			</FormGroup>
																		) : (
																			<FormGroup className="mt-2">
																				<Label className="small">{t("notification.daily_summary_at")}</Label>
																				<Select
																					isSearchable
																					isDisabled={!adminRem.overrideable}
																					options={COMMON_DIGEST_TIMES.map(time => ({
																						value: time,
																						label: prettyTime(time),
																					}))}
																					value={
																						userRem.digestTime
																							? { value: userRem.digestTime, label: prettyTime(userRem.digestTime) }
																							: null
																					}
																					onChange={(opt: any) =>
																						updateReminder(tpl.key, { digestTime: (opt as any).value })
																					}
																					placeholder="Select time…"
																					classNamePrefix="react-select"
																					customStyles={customStyles}
																				/>
																				<FormText color="muted">
																					{t("notification.pick_what_time_you'd_like_the_daily_summary_delivered")}
																				</FormText>
																			</FormGroup>
																		)}
																	</>
																)}
															</div>
															{!adminRem.overrideable && (
																<UncontrolledTooltip placement="top" target={reminderId} delay={{ show: 300, hide: 50 }}>
																	{t("notification.disabled_by_workspace_admin_info_message")}
																</UncontrolledTooltip>
															)}
														</>
													)}
												</div>
											);
										})}
									</div>
								</div>
							)
						})}
					</div>
				)}
			</CardBody>
		</Card>
	);
};

export default withTranslation()(NotificationSettings);
